import React from 'react';

import { useI18n } from '../../../i18n';
import { LANGUAGE_LABELS, SUPPORTED_LANGUAGES } from '../../../i18n/types';
import { useStyles } from './LanguageSelector.styles';

interface IProps {
  /**
   * `compact` — pill for the app header, ES / EN only.
   * `block` — labelled control with helper text, for the profile settings.
   */
  variant?: 'compact' | 'block';
  className?: string;
}

/**
 * Two-option segmented control for the UI language. The choice is written to
 * the account by `I18nProvider`, so it follows the person to any device; the
 * initial value comes from their computer's language.
 */
const LanguageSelector: React.FC<IProps> = ({ variant = 'compact', className }) => {
  const classes = useStyles();
  const { language, locked, setLanguage, saving, saveError, t } = useI18n();

  const control = (
    <div
      className={`${classes.root} ${variant === 'compact' ? classes.compact : ''} ${
        locked ? classes.locked : ''
      } ${
        className ?? ''
      }`}
      role="group"
      aria-label={t('language.aria')}
      // Asset Control is Spanish-only for PROCOMER; the tooltip says why the
      // switch does nothing there.
      aria-disabled={locked || undefined}
      title={locked ? t('language.lockedHint') : undefined}
    >
      {SUPPORTED_LANGUAGES.map((option) => {
        const isActive = option === language;
        return (
          <button
            key={option}
            type="button"
            className={`${classes.option} ${isActive ? classes.optionActive : ''}`}
            onClick={() => setLanguage(option)}
            disabled={locked || (saving && !isActive)}
            aria-pressed={isActive}
            // The long name is the accessible name; the button itself shows
            // the two-letter code so the control stays small in the header.
            aria-label={LANGUAGE_LABELS[option].name}
            title={locked ? t('language.lockedHint') : LANGUAGE_LABELS[option].name}
            lang={option}
          >
            {variant === 'block'
              ? LANGUAGE_LABELS[option].name
              : LANGUAGE_LABELS[option].short}
          </button>
        );
      })}
    </div>
  );

  if (variant === 'compact') {
    return control;
  }

  return (
    <div className={classes.block}>
      <p className={classes.blockLabel}>{t('language.label')}</p>
      {control}
      <p className={classes.blockHint}>{t('profile.preferences.languageHint')}</p>
      {saveError && <p className={classes.error}>{t('language.saveError')}</p>}
    </div>
  );
};

export default LanguageSelector;
